import { DOCUMENT } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { UserSubscriptionLevelsEnum } from "src/@core/models/enum/Enums";
import { AuthService } from "src/@core/services/auth.service";
import { CommandPaletteRegistryService } from "../../shared/command-palette/command-palette-registry.service";
import { CommandPaletteService } from "../../shared/command-palette/command-palette.service";
import { APP_QUICK_NAV_ROUTES } from "../../shared/navigation/app-navigation-shortcuts";
import { resolveMaterialIconName } from "../../shared/navigation/material-icon-fallbacks";
import { resolveUserDisplayName, resolveUserFacilityName, resolveUserRoleLabel } from "../../shared/user/user-identity";

interface QuickNavItem {
  label: string;
  icon: string;
  route: string;
}

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  showHomeButton = false;
  quickNavItems: QuickNavItem[] = [];

  private routerSub?: Subscription;

  constructor(
    @Inject(DOCUMENT) private readonly documentRef: Document,
    public readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly commandPaletteService: CommandPaletteService,
    private readonly commandPaletteRegistry: CommandPaletteRegistryService
  ) {}

  ngOnInit(): void {
    this.syncHeaderState();
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncHeaderState();
      }
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  callFullscreen(): void {
    const doc = this.documentRef as any;
    const root = this.documentRef.documentElement as any;
    if (!doc.fullscreenElement && !doc.mozFullScreenElement && !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
      (root.requestFullscreen || root.msRequestFullscreen || root.mozRequestFullScreen || root.webkitRequestFullscreen)?.call(root);
      return;
    }

    (doc.exitFullscreen || doc.msExitFullscreen || doc.mozCancelFullScreen || doc.webkitExitFullscreen)?.call(doc);
  }

  goToNewsFeed(): void {
    this.router.navigate(["/community-feed"]);
  }

  openCommandPalette(): void {
    this.commandPaletteService.open();
  }

  logout(): void {
    this.authService.logout();
  }

  getUserDisplayName(user: any): string {
    return resolveUserDisplayName(user, "User");
  }

  getUserRoleLabel(user: any): string {
    return resolveUserRoleLabel(user);
  }

  getUserFacilityName(user: any): string {
    return resolveUserFacilityName(user);
  }

  getUserEmail(user: any): string {
    return String(user?.email ?? user?.Email ?? "").trim();
  }

  getUserAvatar(user: any): string {
    return user?.personalImagePath || "assets/images/user.jpg";
  }

  isLaboratoryLevelUser(user: any): boolean {
    const levelId = Number(
      user?.userSubscriptionLevelId ??
        user?.UserSubscriptionLevelId ??
        user?.subscriptionLevelId ??
        user?.SubscriptionLevelId ??
        user?.userSubscriptionLevel?.id ??
        user?.userLaboratorySubscriptionDtos?.[0]?.userSubscriptionLevelId ??
        user?.userLaboratorySubscriptionDtos?.[0]?.userSubscriptionLevel?.id
    );
    if (levelId === UserSubscriptionLevelsEnum.LaboratoryLevel) {
      return true;
    }

    return this.getNormalizedRoles(user).includes("laboratorylevel");
  }

  private getNormalizedRoles(user: any): string[] {
    const rawRoles = user?.userRoles ?? user?.roles ?? user?.role ?? [];
    const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
    return roles
      .map((role) => {
        if (typeof role === "string") {
          return role;
        }
        return role?.name ?? role?.roleName ?? role?.value ?? "";
      })
      .filter(Boolean)
      .map((role) => String(role).toLowerCase().replace(/[\s_-]+/g, ""));
  }

  private syncHeaderState(): void {
    this.showHomeButton = !this.router.url.includes("/launchpad");
    this.quickNavItems = this.buildQuickNavItems();
    this.cdr.markForCheck();
  }

  private buildQuickNavItems(): QuickNavItem[] {
    const visibleCommandsByRoute = new Map(this.commandPaletteRegistry.getVisibleCommands().map((command) => [command.route, command]));
    return APP_QUICK_NAV_ROUTES
      .map((route) => visibleCommandsByRoute.get(route))
      .filter((command): command is NonNullable<typeof command> => !!command)
      .map((command) => ({
        label: command.label,
        icon: resolveMaterialIconName(command.icon),
        route: command.route,
      }));
  }
}
